import { BadgeBg } from 'components/base/Badge';

// Format status label for display by converting underscores to spaces and applying proper capitalization
export const formatStatusLabel = (label: string): string => {
  if (!label) return 'Unknown';
  
  // Replace underscores with spaces and capitalize each word
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper functions to determine badge type and icon based on order status code
export const getOrderStatusConfig = (code: string | undefined): { type: string; icon: string; label?: string } => {
  if (!code) return { type: 'secondary', icon: 'help-circle' };
  
  const statusCode = code.toLowerCase();
  
  // Map order status codes to badge configurations
  switch (statusCode) {
    case 'complete':
    case 'completed':
      return { type: 'success', icon: 'check-circle', label: 'Completed' };
    
    case 'processing':
      return { type: 'warning', icon: 'clock', label: 'Processing' };
    
    case 'pending':
      return { type: 'info', icon: 'clock', label: 'Pending' };
    
    case 'closed':
      return { type: 'secondary', icon: 'lock', label: 'Closed' };
    
    case 'canceled':
      return { type: 'danger', icon: 'x-circle', label: 'Canceled' };
    
    case 'returned':
      return { type: 'danger', icon: 'rotate-ccw', label: 'Returned' };
    
    case 'received':
      return { type: 'success', icon: 'check', label: 'Received' };
    
    case 'transit':
    case 'en_cour_d_enlevement':
      return { type: 'info', icon: 'truck', label: 'In Transit' };
    
    case 'en_cour_de_preparation':
      return { type: 'warning', icon: 'package', label: 'Being Prepared' };
    
    case 'prepared':
      return { type: 'success', icon: 'package', label: 'Prepared' };
    
    case 'receptionner_au_central':
      return { type: 'success', icon: 'home', label: 'Received at Central' };
    
    case 'enlever':
      return { type: 'info', icon: 'arrow-up', label: 'Collected' };
    
    case 'transferer_au_central':
      return { type: 'info', icon: 'refresh-cw', label: 'Transferred to Central' };
    
    case 'en_consolidation':
      return { type: 'primary', icon: 'layers', label: 'In Consolidation' };
      
    case 'new':
      return { type: 'info', icon: 'star', label: 'New' };
      
    default:
      // For any other status, format the label and use a neutral style
      return { 
        type: 'secondary', 
        icon: 'help-circle',
        label: formatStatusLabel(code)
      };
  }
};

// Helper functions to determine badge type and icon based on payment status code
export const getPaymentStatusConfig = (code: string | undefined): { type: string; icon: string; label?: string } => {
  if (!code) return { type: 'secondary', icon: 'help-circle' };
  
  const statusCode = code.toLowerCase();
  
  // Map payment status codes to badge configurations
  switch (statusCode) {
    case 'paid':
    case '1':
      return { type: 'success', icon: 'credit-card', label: 'Paid' };
    
    case '0':
      return { type: 'warning', icon: 'clock', label: 'Pending' };
    
    case '2':
      return { type: 'info', icon: 'pause-circle', label: 'On Hold' };
    
    case '3':
      return { type: 'primary', icon: 'refresh-cw', label: 'Refunded' };
    
    case '4':
      return { type: 'danger', icon: 'x-circle', label: 'Canceled' };

    
    default:
      // For any other status, format the label and use a neutral style
      return { 
        type: 'secondary', 
        icon: 'help-circle',
        label: formatStatusLabel(code)
      };
  }
};

// Helper function to get payment method configuration
export const getPaymentMethodConfig = (code: string | undefined): { type: string; icon: string; label?: string } => {
  if (!code) return { type: 'info', icon: 'credit-card' };
  
  const methodCode = code.toLowerCase();
  
  // Map payment method codes to badge configurations
  switch (methodCode) {
    case 'cashondelivery':
      return { type: 'success', icon: 'dollar-sign', label: 'Cash On Delivery' };
    
    case 'clictopay_gateway':
      return { type: 'info', icon: 'credit-card', label: 'Credit Card' };
    
    default:
      return { type: 'info', icon: 'credit-card' };
  }
};

// General status helper function for backward compatibility
export const getStatusType = (code: string | undefined): string => {
  if (!code) return 'secondary';
  
  // Map status codes to badge types using the new helper functions
  const statusCode = code.toLowerCase();
  
  if (['paid', 'complete', 'completed', 'received', 'prepared'].includes(statusCode)) {
    return 'success';
  } else if (['pending', 'processing', 'en_cour_de_preparation'].includes(statusCode)) {
    return 'warning';
  } else if (['canceled', 'failed', '4', 'returned'].includes(statusCode)) {
    return 'danger';
  } else if (['transit', 'en_cour_d_enlevement', 'enlever', 'transferer_au_central'].includes(statusCode)) {
    return 'info';
  } else if (['hold', 'receptionner_au_central', 'en_consolidation'].includes(statusCode)) {
    return 'primary';
  } else {
    return 'secondary';
  }
};

// General icon helper function for backward compatibility
export const getStatusIcon = (code: string | undefined): string => {
  if (!code) return 'help-circle';
  
  // Map status codes to icons using the new helper functions
  const statusCode = code.toLowerCase();
  
  if (['paid', 'complete', 'completed', 'received', 'prepared'].includes(statusCode)) {
    return 'check';
  } else if (['pending', 'processing', 'hold'].includes(statusCode)) {
    return 'clock';
  } else if (['canceled', 'failed', '4'].includes(statusCode)) {
    return 'x';
  } else if (['returned', 'refunded'].includes(statusCode)) {
    return 'rotate-ccw';
  } else if (['transit', 'en_cour_d_enlevement'].includes(statusCode)) {
    return 'truck';
  } else if (['en_cour_de_preparation', 'prepared'].includes(statusCode)) {
    return 'package';
  } else if (['receptionner_au_central'].includes(statusCode)) {
    return 'home';
  } else if (['enlever'].includes(statusCode)) {
    return 'arrow-up';
  } else if (['transferer_au_central'].includes(statusCode)) {
    return 'refresh-cw';
  } else if (['en_consolidation'].includes(statusCode)) {
    return 'layers';
  } else {
    return 'help-circle';
  }
};

// Function to determine next status based on current status
export const getNextStatus = (currentStatus: string): string | null => {
  const status = currentStatus.toLowerCase();
  
  switch (status) {
    case 'processing':
      return 'En_cour_de_preparation';
    case 'being prepared':
      return 'prepared';
    
    default:
      return null;
  }
};
